<section [ngSwitch]="currentStep" class="max-w-2xl">
  <ng-container *ngSwitchCase="'preconnect'">
    <ng-container *ngTemplateOutlet="preConnectTemplate"></ng-container>
  </ng-container>

  <ng-container *ngSwitchCase="'preconnect-limited'">
    <ng-container *ngTemplateOutlet="preconnectLimitedTemplate"></ng-container>
  </ng-container>

  <ng-container *ngSwitchCase="'connect-pending'">
    <ng-container *ngTemplateOutlet="connectPendingTemplate"></ng-container>
  </ng-container>
</section>

<!-- Step 1 - Pre-OAuth -->
<ng-template #preConnectTemplate>
  <div class="flex gap-6 justify-center items-center mb-8">
    <img src="/assets/images/hospitable.png" class="h-8" />
    <mat-icon class="scale-150 text-smoke-darker">sync_alt</mat-icon>
    <img src="/assets/images/connected-accounts/airbnb.svg" class="h-8" />
  </div>

  <p class="mb-6">
    <strong>Before you connect,</strong>
    make sure you are logged in to Airbnb with the account you want to connect to Hospitable.com.
  </p>

  <p class="mb-1"><strong>After connecting</strong></p>

  <ul class="mb-8 list list__looser">
    <li>You'll manage all of your pricing and availability in {{ 'CORE.PRODUCT_NAME_SHORT' | transloco }}</li>
    <ul class="list list__looser">
      <li>If you are using Airbnb's smart pricing, it will be disabled</li>
      <li>Third-party dynamic pricing apps connected to Hospitable are not impacted</li>
      <li>Any third-party connected calendars (i.e. iCals) will be removed</li>
    </ul>
    <li>
      Accounts with properties outside of North America may have their Airbnb service fee changed to a host-only fee
    </li>
  </ul>

  <div [class.disabled]="!connectUrls?.pms" class="flex justify-end gap-4 mt-12 mb-12">
    <sbnb-button type="secondary" routerLink="/accounts">Cancel</sbnb-button>
    <sbnb-button (clicked)="connectToAirbnb(connectUrls.pms)">Connect</sbnb-button>
  </div>

  <div class="border-t border-smoke-darker text-sm">
    <p class="!text-night">
      <a
        class="link"
        href="https://help.hospitable.com/en/articles/9853198-new-upgraded-airbnb-connectivity"
        target="_blank">
        Learn more
      </a>
      about what changes when connecting Airbnb to a software provider.
    </p>

    <p class="!text-night">
      <span class="strong">Already connected to other management software?</span>
      You can still use Hospitable in a limited capacity by connecting
      <a class="link" (click)="currentStep = 'preconnect-limited'">here</a>
    </p>
  </div>
</ng-template>

<!-- Step 1 - Limited Connection (Ops scope) -->
<ng-template #preconnectLimitedTemplate>
  <div class="flex gap-2 items-center">
    <img src="/assets/iconography/airbnb.svg" class="h-6" alt="Airbnb logo" />
    <h2 class="nomargin">{{ 'CORE.PRODUCT_NAME_SHORT' | transloco }} for software-connected hosts</h2>
  </div>

  <p>
    {{ 'CORE.PRODUCT_NAME_SHORT' | transloco }} can connect to your Airbnb account even if you're connected to another
    software provider.
    <a
      class="link"
      href="https://help.hospitable.com/en/articles/8889941-using-other-software-alongside-airbnb"
      target="_blank">
      Learn more
    </a>
  </p>

  <!-- features table-->
  <div class="flex gap-8 mt-8">
    <div class="flex-1">
      <strong class="mt-4">{{ 'CORE.PRODUCT_NAME_SHORT' | transloco }} cannot</strong>

      <ul class="limitations-list">
        <li>Manage calendar availability</li>
        <li>Sync calendars across other channels</li>
        <li>Manage availability rules</li>
      </ul>
    </div>

    <div class="flex-1">
      <strong class="mt-4">{{ 'CORE.PRODUCT_NAME_SHORT' | transloco }} can</strong>

      <ul class="list !pl-0 !mt-2">
        <li class="flex gap-2 items-center">
          <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
          Import listings, reservations, and pricing
        </li>
        <li class="flex gap-2 items-center">
          <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
          Manage prices
        </li>
        <li class="flex gap-2 items-center">
          <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
          Upgrade guest messaging with automation and AI
        </li>
      </ul>
    </div>
  </div>

  <p>
    If you want to use {{ 'CORE.PRODUCT_NAME_SHORT' | transloco }} as your primary software provider,
    <a
      class="link"
      href="https://help.hospitable.com/en/articles/8889868-reconnect-disconnect-your-airbnb-account#h_70d2774e48"
      target="_blank">
      disconnect from your other provider
    </a>
    and upgrade to our full-service connection.
  </p>

  <div class="flex justify-end gap-4 mt-7">
    <sbnb-button type="secondary" (clicked)="currentStep = 'preconnect'">Switch to full connection</sbnb-button>
    <sbnb-button (clicked)="connectToAirbnb(connectUrls.legacy)">Limited connection</sbnb-button>
  </div>
</ng-template>

<ng-template #connectPendingTemplate>
  <div @fadeInUpAnimation>
    <h2 class="mt-4 mb-0">We've opened Airbnb in a new tab</h2>
    <p *ngIf="connectPendingStage !== 'connected'">Go to the new tab to allow the connection</p>
  </div>

  <div *ngIf="connectPendingStage !== 'newtab'" @fadeInUpAnimation>
    <div
      *ngIf="connectPendingStage !== 'connected'"
      class="flex border-smoke rounded border p-4 mt-6 bg-smoke-lighter text-sm gap-2">
      <mat-spinner diameter="20"></mat-spinner>
      <span class="ml-2">Checking connection status...</span>
    </div>

    <div
      *ngIf="connectPendingStage === 'connected'"
      class="flex border-smoke rounded border p-4 mt-6 bg-smoke-lighter text-sm gap-0 items-center">
      <mat-icon class="grass-darker">check</mat-icon>
      <span class="ml-2">
        <strong class="grass-darker">Connection successful.</strong>
        You're all set! You can close this tab.
      </span>
    </div>
  </div>

  <div *ngIf="connectPendingStage === 'checking-with-redirect'" @fadeInUpAnimation>
    <p class="mt-16">
      <strong>Having issues?</strong>
      <br />

      If you want to use Hospitable while connected to another Airbnb software provider, you should
      <a class="link" (click)="currentStep = 'preconnect-limited'">connect here</a>
    </p>
  </div>

  <div *ngIf="connectPendingStage === 'error'" @fadeInUpAnimation>
    <!-- TODO: We're not totally sure what will come back here yet, once the backend is merged this can be updated  -->
    <span>An error occurred while connecting to Airbnb.</span>
  </div>
</ng-template>
